import {
  DEFAULT_DATE_TIME_FORMAT_PREFERENCE,
  DateTimeFormatPreference,
  DisplayZone,
  ResolvedDateTimeFormat,
  resolveFormats,
} from "./format";

/**
 * Process-wide holder for the active resolved date/time format.
 *
 * The user's preference is loaded asynchronously from backend config by
 * {@link DateTimeFormatProvider}, but a great many formatting call sites are
 * pure functions with no access to React context (chart-axis callbacks,
 * ag-grid value formatters, the `shared/time` helpers). This registry is the
 * bridge: the provider pushes the loaded preference in via
 * {@link setActiveDateTimeFormat}, and pure code reads the current formatter
 * out via {@link getActiveDateTimeFormat}. React components subscribe through
 * {@link subscribeDateTimeFormat} (wrapped by `useDateTimeFormat`) so they
 * re-render when the preference changes.
 *
 * Until the provider runs, the registry holds the locale default, so anything
 * rendered before config loads is still sensible.
 */
let active: ResolvedDateTimeFormat = resolveFormats(
  DEFAULT_DATE_TIME_FORMAT_PREFERENCE,
);

/**
 * The active preference read on the UTC clock, built on first request and
 * dropped when the preference changes. Held here rather than resolved per
 * caller so each (preference, zone) pair has one identity, which
 * `useSyncExternalStore` snapshots and memo dependencies rely on.
 */
let activeUtc: ResolvedDateTimeFormat | null = null;

const listeners = new Set<() => void>();

/**
 * The currently-active resolved formatter, reading instants in `zone` (the
 * viewer's local zone when omitted). Never null; the same object until the
 * preference changes.
 */
export function getActiveDateTimeFormat(
  zone?: DisplayZone,
): ResolvedDateTimeFormat {
  if (zone === undefined) {
    return active;
  }
  activeUtc ??= resolveFormats(active.preference, zone);
  return activeUtc;
}

/**
 * Replace the active formatter from a (already-normalized) preference and
 * notify subscribers. Resolving is skipped when the preference is unchanged,
 * so redundant config refetches don't churn the React tree.
 */
export function setActiveDateTimeFormat(
  preference: DateTimeFormatPreference,
): void {
  const current = active.preference;
  if (
    current.datePreset === preference.datePreset &&
    current.clock === preference.clock
  ) {
    return;
  }
  active = resolveFormats(preference);
  activeUtc = null;
  listeners.forEach((listener) => listener());
}

/**
 * Subscribe to active-format changes. Returns an unsubscribe function.
 * Designed for `useSyncExternalStore`.
 */
export function subscribeDateTimeFormat(listener: () => void): () => void {
  listeners.add(listener);
  return () => {
    listeners.delete(listener);
  };
}
